/**
 * DiscoveryCallCTA — Prominent CTA section for all main pages.
 * "Book a Free 15-Min Discovery Call" with phone and WhatsApp link.
 */
import { Phone, MessageCircle, Calendar } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { Link } from 'react-router-dom';

const CTA_I18N: Record<string, { heading: string; sub: string; phone: string; whatsapp: string; book: string }> = {
  en: { heading: 'Book a Free 15-Min Discovery Call', sub: 'Speak with a BPO & IT expert — no commitment, just answers.', phone: 'Call Us', whatsapp: 'WhatsApp Us', book: 'Book a Call' },
  fr: { heading: 'Réservez un Appel Découverte Gratuit de 15 Min', sub: 'Parlez avec un expert BPO & IT — sans engagement, juste des réponses.', phone: 'Appelez-nous', whatsapp: 'WhatsApp', book: 'Réserver un Appel' },
  es: { heading: 'Reserve una Llamada de Descubrimiento Gratis de 15 Min', sub: 'Hable con un experto BPO & IT — sin compromiso, solo respuestas.', phone: 'Llámenos', whatsapp: 'WhatsApp', book: 'Reservar Llamada' },
  de: { heading: 'Buchen Sie ein kostenloses 15-Min Discovery-Gespräch', sub: 'Sprechen Sie mit einem BPO & IT-Experten — unverbindlich.', phone: 'Anrufen', whatsapp: 'WhatsApp', book: 'Gespräch Buchen' },
  ar: { heading: 'احجز مكالمة اكتشاف مجانية لمدة 15 دقيقة', sub: 'تحدث مع خبير BPO و IT — بدون التزام.', phone: 'اتصل بنا', whatsapp: 'واتساب', book: 'احجز مكالمة' },
  it: { heading: 'Prenota una Chiamata Gratuita di 15 Min', sub: 'Parla con un esperto BPO & IT — senza impegno.', phone: 'Chiamaci', whatsapp: 'WhatsApp', book: 'Prenota' },
  pt: { heading: 'Agende uma Chamada Gratuita de 15 Min', sub: 'Fale com um especialista BPO & IT — sem compromisso.', phone: 'Ligue', whatsapp: 'WhatsApp', book: 'Agendar' },
  nl: { heading: 'Boek een Gratis 15-Min Discovery Call', sub: 'Spreek met een BPO & IT-expert — vrijblijvend.', phone: 'Bel Ons', whatsapp: 'WhatsApp', book: 'Boek een Gesprek' },
  pl: { heading: 'Zarezerwuj Bezpłatną 15-Min Rozmowę', sub: 'Porozmawiaj z ekspertem BPO & IT — bez zobowiązań.', phone: 'Zadzwoń', whatsapp: 'WhatsApp', book: 'Zarezerwuj' },
  ru: { heading: 'Закажите бесплатный 15-мин звонок', sub: 'Поговорите с экспертом BPO & IT — без обязательств.', phone: 'Позвонить', whatsapp: 'WhatsApp', book: 'Записаться' },
};

const PHONE_MOROCCO = '+212537373777';
const PHONE_UAE = '+971558170508';
const WHATSAPP_URL = `https://wa.me/${PHONE_UAE.replace(/[^0-9]/g, '')}`;

export function DiscoveryCallCTA() {
  const { language, isRTL } = useLanguage();
  const t = CTA_I18N[language] || CTA_I18N.en;

  return (
    <section
      dir={isRTL ? 'rtl' : 'ltr'}
      className="bg-primary/5 border-y border-primary/10 py-12 md:py-16"
      aria-label={t.heading}
    >
      <div className="container max-w-4xl text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
          <Calendar className="inline-block w-7 h-7 mr-2 text-primary align-middle" aria-hidden="true" />
          {t.heading}
        </h2>
        <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">{t.sub}</p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={`tel:${PHONE_MOROCCO}`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold shadow-lg hover:opacity-90 transition-opacity"
          >
            <Phone className="w-5 h-5" aria-hidden="true" />
            {t.phone}: +212 5 37 37 37 77
          </a>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-green-600 text-white font-semibold shadow-lg hover:opacity-90 transition-opacity"
          >
            <MessageCircle className="w-5 h-5" aria-hidden="true" />
            {t.whatsapp}
          </a>

          <Link
            to={`/${language}/contact`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-primary text-primary font-semibold hover:bg-primary/10 transition-colors"
          >
            <Calendar className="w-5 h-5" aria-hidden="true" />
            {t.book}
          </Link>
        </div>
      </div>
    </section>
  );
}
